/**
 * En jeu : deux boutons posés contre le HUD (Amis, Menu), le panneau Amis (invitations reçues,
 * demandes, amis à inviter dans la partie en cours) et le menu de la partie (Échap) :
 * reprendre, inviter, copier le lien, paramètres (gérant), retour au menu principal.
 *
 * Quitter la partie recharge la page : le contexte WebGPU et les ressources du moteur sont
 * libérés d'un coup, et l'amorçage repart sur le menu.
 */
import { signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import { Crown, Link, LogOut, Menu as MenuIcon, Play, Settings, UserPlus, Users, X } from 'lucide-preact';
import { hud } from '../hud/context';
import { toast, ui } from '../hud/ui-state';
import { lobby } from './lobby';
import { menu, menuActions } from './state';
import { copyInvite, inviteFriend } from './actions';
import { AddFriendForm, FriendRow, InviteCard, RequestRow } from './social';
import { MenuDialogs } from './dialogs';
import { Avatar, Btn, Empty, inviteLink } from './ui';

const panel = signal<'social' | 'menu' | null>(null);
/** Amis invités depuis cette partie (bouton « Invité »). */
const invited = signal<Set<string>>(new Set());

/** URL du menu : mêmes paramètres (rendu, mesures) sans la partie. */
export function menuUrl(): string {
  const url = new URL(location.href);
  url.searchParams.delete('world');
  return url.toString();
}

function goToWorld(worldId: string): void {
  location.assign(inviteLink(worldId) + keepParams());
}

/** Conserve les paramètres de diagnostic (?renderer=webgl, ?perf...) en changeant de partie. */
function keepParams(): string {
  const cur = new URLSearchParams(location.search);
  cur.delete('world');
  const s = cur.toString();
  return s ? `&${s}` : '';
}

function currentWorld(): { id: string; name: string } | null {
  const r = hud.store?.replica;
  const w = hud.store?.welcome;
  if (!r || !w) return null;
  return { id: w.worldId, name: r.world.name };
}

/** Position des boutons : sous l'horloge (`top`) ou au-dessus de la barre d'état (`bottom`). */
interface Anchor {
  top: number | null;
  bottom: number | null;
  right: number;
}

/** Largeur réservée aux deux boutons, pour savoir si la barre d'outils passe dessous. */
const BAR_W = 150;

function sameAnchor(a: Anchor, b: Anchor): boolean {
  return a.top === b.top && a.bottom === b.bottom && a.right === b.right;
}

/**
 * Place les boutons à côté du HUD de jeu, quelle que soit sa disposition : sous la carte de
 * l'horloge quand elle est en haut de l'écran, au-dessus de la barre d'état (coin droit) quand
 * l'horloge est dans une barre en bas. Si la barre d'outils déborde jusque sous les boutons
 * (écran étroit), ils passent au-dessus d'elle. Relu deux fois par seconde : le HUD change
 * de taille selon l'heure, les fenêtres et la largeur.
 */
function useAnchor(): Anchor {
  const [pos, setPos] = useState<Anchor>({ top: null, bottom: 112, right: 16 });
  useEffect(() => {
    const place = () => {
      const el = document.querySelector('.g-clock');
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) return; // HUD masqué : on garde la dernière place
      const W = window.innerWidth;
      const H = window.innerHeight;
      let next: Anchor;
      if (r.top + r.height / 2 < H / 2) {
        next = { top: Math.round(r.bottom + 10), bottom: null, right: Math.max(8, Math.round(W - r.right)) };
      } else {
        let top = (el.closest('.g-top') ?? el).getBoundingClientRect().top;
        const dock = document.querySelector('.g-dock')?.getBoundingClientRect();
        if (dock && dock.width && dock.right > W - 16 - BAR_W) top = Math.min(top, dock.top);
        next = { top: null, bottom: Math.max(8, Math.round(H - top + 10)), right: 16 };
      }
      setPos((prev) => (sameAnchor(prev, next) ? prev : next));
    };
    const t = setInterval(place, 500);
    window.addEventListener('resize', place);
    place();
    return () => {
      clearInterval(t);
      window.removeEventListener('resize', place);
    };
  }, []);
  return pos;
}

/** Style d'un élément posé contre les boutons : au-dessous d'eux, ou au-dessus en bas d'écran. */
function beside(pos: Anchor, offset: number): Record<string, string> {
  return pos.top !== null ? { top: `${pos.top + offset}px`, right: `${pos.right}px` } : { bottom: `${(pos.bottom ?? 0) + offset}px`, right: `${pos.right}px` };
}

function SocialPanel(props: { world: { id: string; name: string } | null }) {
  const s = lobby.social.value;
  const online = s.friends.filter((f) => f.presence.state !== 'offline');
  const offline = s.friends.filter((f) => f.presence.state === 'offline');
  const inv = invited.value;
  const invite = async (f: { id: string; name: string }) => {
    if (!props.world) return;
    if (await inviteFriend(f, props.world)) invited.value = new Set([...inv, f.id]);
  };
  return (
    <div class="mn-ig-panel" role="dialog" aria-label="Amis">
      <div class="mn-side-head">
        <Users size={15} />
        <span class="mn-side-title">Amis</span>
        <span class="mn-muted mn-small">{online.length} en ligne</span>
        <button type="button" class="mn-ibtn" aria-label="Fermer" onClick={() => (panel.value = null)}>
          <X size={15} />
        </button>
      </div>
      <AddFriendForm compact />
      <div class="mn-side-scroll">
        {s.invites.length > 0 && (
          <div class="mn-side-group">
            <p class="mn-side-label">Invitations · {s.invites.length}</p>
            {s.invites.map((i) => (
              <InviteCard key={i.id} inv={i} onJoin={goToWorld} />
            ))}
          </div>
        )}
        {s.incoming.length > 0 && (
          <div class="mn-side-group">
            <p class="mn-side-label">Demandes reçues · {s.incoming.length}</p>
            {s.incoming.slice(0, 5).map((r) => (
              <RequestRow key={r.id} r={r} dir="in" compact />
            ))}
          </div>
        )}
        <div class="mn-side-group">
          {s.friends.length === 0 ? (
            <Empty icon={Users} title="Pas encore d’amis">
              Ajoutez un joueur par son nom pour l’inviter dans cette partie.
            </Empty>
          ) : (
            <>
              <p class="mn-side-label">Inviter dans « {props.world?.name ?? 'cette partie'} »</p>
              {[...online, ...offline].map((f) => (
                <FriendRow key={f.id} f={f} mode="game" world={props.world} invited={inv.has(f.id)} onInvite={() => void invite(f)} onJoin={goToWorld} />
              ))}
            </>
          )}
        </div>
      </div>
      {props.world && (
        <button type="button" class="mn-ig-link" onClick={() => void copyInvite(props.world!.id)}>
          <Link size={13} /> Copier le lien de la partie
        </button>
      )}
    </div>
  );
}

function GameMenu(props: { world: { id: string; name: string } | null }) {
  const store = hud.store;
  const r = store?.replica;
  const me = store?.welcome;
  const owner = !!store?.isOwner;
  const players = r ? r.world.players.filter((p) => p.online) : [];
  const ai = r ? r.world.postes.filter((p) => p.controller === 'ai') : [];
  const [busy, setBusy] = useState(false);
  const openSettings = async () => {
    if (!props.world) return;
    setBusy(true);
    try {
      const res = await lobby.req({ op: 'world.get', id: props.world.id });
      menu.dialog.value = { k: 'settings', server: res.server };
    } catch (e) {
      toast('danger', 'Paramètres indisponibles', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="mn-modal mn-ig-modal" onMouseDown={(e) => e.target === e.currentTarget && (panel.value = null)}>
      <div class="mn-gm" role="dialog" aria-modal="true" aria-label="Menu de la partie">
        <div class="mn-gm-main">
          <p class="mn-kicker">Partie en cours</p>
          <h2 class="mn-gm-title">{r?.station.name ?? 'SnowChain'}</h2>
          <p class="mn-muted">
            {props.world?.name} · {owner ? 'vous gérez la station' : 'vous observez'}
          </p>
          <div class="mn-gm-btns">
            <button type="button" class="mn-gm-btn mn-gm-primary" onClick={() => (panel.value = null)}>
              <Play size={18} /> Reprendre <kbd>Échap</kbd>
            </button>
            <button type="button" class="mn-gm-btn" onClick={() => (panel.value = 'social')}>
              <UserPlus size={18} /> Inviter des amis
              {lobby.attention.value > 0 && <span class="mn-badge">{lobby.attention.value}</span>}
            </button>
            <button type="button" class="mn-gm-btn" disabled={!props.world} onClick={() => props.world && void copyInvite(props.world.id)}>
              <Link size={18} /> Copier le lien de la partie
            </button>
            {owner && (
              <button type="button" class="mn-gm-btn" disabled={busy} onClick={() => void openSettings()}>
                <Settings size={18} /> Paramètres de la partie
              </button>
            )}
            <button type="button" class="mn-gm-btn" onClick={() => location.assign(menuUrl())}>
              <MenuIcon size={18} /> Quitter vers le menu principal
            </button>
            <button type="button" class="mn-gm-btn mn-gm-quiet" onClick={() => menuActions.logout()}>
              <LogOut size={18} /> Se déconnecter
            </button>
          </div>
        </div>
        <div class="mn-gm-side">
          <p class="mn-side-label">Dans la partie · {players.length}</p>
          <ul class="mn-people">
            {players.map((p) => (
              <li key={p.id}>
                <Avatar id={p.id} name={p.name} size={26} presence="game" />
                <span>{p.id === me?.playerId ? `${p.name} (vous)` : p.name}</span>
                <span class="mn-pill">{p.role === 'observer' ? 'Observateur' : 'Gérant'}</span>
                {r?.world.hostId === p.id && <Crown size={13} class="mn-gold" />}
              </li>
            ))}
            {ai.map((p) => (
              <li key={p.role}>
                <Avatar id={p.role} name={p.name} size={26} />
                <span>{p.name}</span>
                <span class="mn-pill">IA</span>
              </li>
            ))}
          </ul>
          <p class="mn-small mn-muted">Le temps ne s’arrête pas quand ce menu est ouvert : la partie continue pour tout le monde.</p>
        </div>
      </div>
    </div>
  );
}

/** Partie perdue en cours de route (supprimée, complète à la reconnexion, session expirée). */
function ConnectionGuard() {
  void ui.tick.value;
  const st = hud.store?.status;
  if (st !== 'world-error' && st !== 'auth-error') return null;
  return (
    <div class="mn-modal">
      <div class="mn-dialog" role="alertdialog" aria-modal="true" aria-label="Partie indisponible">
        <div class="mn-dialog-head">
          <h2>{st === 'auth-error' ? 'Session expirée' : 'Partie indisponible'}</h2>
        </div>
        <div class="mn-dialog-body">
          <p>{st === 'auth-error' ? 'Reconnectez-vous pour reprendre vos parties.' : hud.store?.statusDetail || 'La connexion à cette partie a été refusée.'}</p>
        </div>
        <div class="mn-dialog-foot">
          <Btn kind="primary" icon={MenuIcon} onClick={() => location.assign(menuUrl())}>
            Retour au menu
          </Btn>
        </div>
      </div>
    </div>
  );
}

/** Éléments sociaux superposés au HUD de jeu. */
export function InGameSocial() {
  void ui.tick.value;
  const pos = useAnchor();
  const world = currentWorld();
  const which = panel.value;
  const s = lobby.social.value;
  const onlineFriends = s.friends.filter((f) => f.presence.state !== 'offline').length;
  const attention = lobby.attention.value;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Escape' || e.ctrlKey || e.metaKey || e.altKey) return;
      if (menu.dialog.value) return; // la fenêtre ouverte se ferme d'abord
      if (panel.value) {
        panel.value = null;
        e.stopImmediatePropagation();
        return;
      }
      const t = e.target as HTMLElement | null;
      if (t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA') return;
      // Échap ferme d'abord ce qui est ouvert dans le HUD (construction, sélection, fenêtres).
      if (ui.build.value || ui.buildMenu.value || ui.selection.value || ui.office.value || ui.freeCam.value) return;
      panel.value = 'menu';
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  return (
    <>
      <div class="mn-ig-bar" style={beside(pos, 0)}>
        <button type="button" class={`mn-ig-btn${which === 'social' ? ' on' : ''}`} onClick={() => (panel.value = which === 'social' ? null : 'social')} title="Amis et invitations" aria-label="Amis et invitations">
          <Users size={16} />
          <span class="mn-ig-count num">{onlineFriends}</span>
          {attention > 0 && <span class="mn-badge mn-ig-badge">{attention}</span>}
        </button>
        <button type="button" class={`mn-ig-btn${which === 'menu' ? ' on' : ''}`} onClick={() => (panel.value = which === 'menu' ? null : 'menu')} title="Menu de la partie (Échap)" aria-label="Menu de la partie">
          <MenuIcon size={16} />
        </button>
      </div>
      {which === 'social' && (
        <div class={`mn-ig-anchor${pos.top === null ? ' mn-ig-up' : ''}`} style={beside(pos, 48)}>
          <SocialPanel world={world} />
        </div>
      )}
      {which === 'menu' && <GameMenu world={world} />}
      <MenuDialogs />
      <ConnectionGuard />
    </>
  );
}
